// src/api/padplannerApi.js
//
// Every call PadPlanner makes, in one file, so its network footprint can be
// read rather than assembled by grepping pages. The server is
// admin-api/routes/padplanner.mjs and, for the template library,
// padplannerLibrary.mjs (the checks: padplannerChecks.mjs; the clearance
// rules: padplannerRules.mjs); every path is gated on PADPLANNER, reads on
// can_view_analytics and writes on can_edit_site_plans (library writes also
// on org_admin).
//
// ── PATHS ARE PLAIN TEMPLATES, QUERIES ARE CONCATENATED OUTSIDE THEM ───────
//
// gen-ui-endpoints.mjs resolves call sites statically and cross-checks them
// against docs/api/ROUTE_INVENTORY.md. A query interpolated into the path
// template resolves to a path the server does not serve, and the cross-check
// stops covering the route (portalApi.js getThread has the long version).

import { fetchWithAuth } from "./fetchWithAuth";

const BASE = "/api/v1/padplanner";

/** A refused call, with the server's `code` attached rather than parsed out of a message. */
export class PadPlannerError extends Error {
  constructor(status, payload) {
    super(payload?.error || `Request failed (${status})`);
    this.name = "PadPlannerError";
    this.status = status;
    this.code = typeof payload?.code === "string" && payload.code ? payload.code : null;
    this.detail = payload?.detail ?? null;
  }
}

async function call(url, options) {
  const res = await fetchWithAuth(url, options);
  let payload = null;
  try { payload = await res.json(); } catch { payload = null; }
  if (!res.ok) throw new PadPlannerError(res.status, payload);
  return payload;
}

const json = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

function withQuery(path, params) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  }
  const q = p.toString();
  return q ? path + "?" + q : path;
}

export function getDashboard() {
  return call(`${BASE}/dashboard`);
}

/** {status, plan_type, owner ('me' | user id), archived, q, cursor, limit} */
export function listPlans(filters = {}) {
  const path = `${BASE}/plans`;
  return call(withQuery(path, filters));
}

/** bbox: [west, south, east, north] */
export function getPerimeters(bbox) {
  const path = `${BASE}/plans/perimeters`;
  return call(withQuery(path, { bbox: bbox.map((n) => n.toFixed(6)).join(",") }));
}

export function getPlan(id) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}`);
}

/** {title, plan_type, target_date?, owner_user_id?, perimeter?, wells?} */
export function createPlan(body) {
  return call(`${BASE}/plans`, json("POST", body));
}

/** Any of {title, plan_type, target_date, owner_user_id}. */
export function updatePlan(id, body) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}`, json("PATCH", body));
}

export function archivePlan(id) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/archive`, { method: "POST" });
}

/**
 * Any of {perimeter, perimeter_source, pad_observation_id, imagery: {dataset_key}|null}.
 * Not local_epsg: since S4a the server derives it from the perimeter on the
 * first feature save, and refuses it here (field_not_writable). With base
 * (the revision token the page holds: the editor's) the write is held to it,
 * 409 revision_changed otherwise; the answer's token is the revision's new one.
 */
export function updateRevision(id, body) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/current`, json("PATCH", body));
}

/** {source: 'rrc'|'manual', label} */
export function putWell(id, api, body = {}) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/current/wells/${encodeURIComponent(api)}`, json("PUT", body));
}

export function deleteWell(id, api) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/current/wells/${encodeURIComponent(api)}`, { method: "DELETE" });
}

/* ── features (S4a; admin-api/routes/padplannerFeatures.mjs) ──────────── */

/** {plan_id, revision: {id, rev_no, frozen, local_epsg}, token, features}. */
export function getFeatures(id) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/current/features`);
}

/**
 * {base, features}: base is the token the last GET or save returned, and
 * features is the COMPLETE list (one left out is removed). Answers
 * {plan, token, local_epsg, change, features}; a stale base is 409
 * revision_changed and nothing is written.
 */
export function saveFeatures(id, body) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/current/features`, json("PUT", body));
}

/**
 * Any revision of the plan, read-only (S6a2): {plan_id, revision: {id, rev_no,
 * frozen, current, local_epsg, perimeter, imagery}, token (current only), features}.
 */
export function getRevisionFeatures(id, revNo) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revNo)}/features`);
}

/**
 * One revision as a GeoJSON file (S6b): {blob, filename, sha256}. profile is
 * "full" or "landowner". The server names the file (no date) and records the
 * export before it answers.
 */
export async function exportPlanGeoJson(id, revNo, { profile = "full" } = {}) {
  const res = await fetchWithAuth(withQuery(`${BASE}/plans/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revNo)}/export.geojson`, { profile }));
  if (!res.ok) {
    let payload = null;
    try { payload = await res.json(); } catch { payload = null; }
    throw new PadPlannerError(res.status, payload);
  }
  return {
    blob: await res.blob(),
    filename: (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] ?? `plan-rev${revNo}-${profile}.geojson`,
    sha256: res.headers.get("X-Content-SHA256"),
    // The revision's status now: a header, so a frozen revision's file never changes.
    status: res.headers.get("X-PadPlanner-Status"),
  };
}

/**
 * An issued revision's plan report (S7c): {blob, filename, sha256}. The PDF
 * stored at Issue, as stored; the server records the view before it answers.
 * Read with the user's token, never through a shareable link.
 */
export async function getPlanReport(id, revNo) {
  const res = await fetchWithAuth(`${BASE}/plans/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revNo)}/report.pdf`);
  if (!res.ok) {
    let payload = null;
    try { payload = await res.json(); } catch { payload = null; }
    throw new PadPlannerError(res.status, payload);
  }
  return {
    blob: await res.blob(),
    filename: (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] ?? `plan-rev${revNo}-report.pdf`,
    sha256: res.headers.get("X-Content-SHA256"),
  };
}

/** Pin an unpinned plan to a published set of its own org, once. Answers {plan}. */
export function pinTemplateSet(id, templateSetId) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/template-set`, json("PUT", { template_set_id: templateSetId }));
}

/** By API number only: 8, 10 or 14 digits, or 5 to 7 leading digits. */
export function lookupWells(q) {
  const path = `${BASE}/wells/lookup`;
  return call(withQuery(path, { q }));
}

/**
 * The projects a plan may carry, {projects: [{id, name}]}: this org's active
 * projects, by the rule the API's project_invalid check applies. Not the
 * project picker's list, which also holds other orgs' QC projects.
 */
export function listProjects() {
  return call(`${BASE}/projects`);
}

export function getPad(observationId) {
  return call(`${BASE}/pads/${encodeURIComponent(observationId)}`);
}

export function getEpochs(planId) {
  const path = `${BASE}/imagery/epochs`;
  return call(withQuery(path, { plan: planId }));
}

/** Epochs at a point, for an outline not saved yet: [lon, lat]. */
export function getEpochsAt([lon, lat]) {
  const path = `${BASE}/imagery/epochs`;
  return call(withQuery(path, { at: `${lon.toFixed(6)},${lat.toFixed(6)}` }));
}

/* ── the template library (S3; admin-api/routes/padplannerLibrary.mjs) ──── */

/** {sets, default_lineage_id, can_manage}. Drafts are listed only to a library admin. */
export function listTemplateSets() {
  return call(`${BASE}/template-sets`);
}

/** {set, templates, can_manage}. A draft is 404 to anyone who cannot manage it. */
export function getTemplateSet(id) {
  return call(`${BASE}/template-sets/${encodeURIComponent(id)}`);
}

// Library writes: org_admin with can_edit_site_plans, never a support session.
// Each answers {set, templates} as the set now is, except discard
// ({discarded, set_id}) and the default ({default_lineage_id}).

/** {name}: a new set, version 1 of a new lineage, a draft. */
export function createTemplateSet(body) {
  return call(`${BASE}/template-sets`, json("POST", body));
}

/** {name}: a draft only. */
export function updateTemplateSet(id, body) {
  return call(`${BASE}/template-sets/${encodeURIComponent(id)}`, json("PATCH", body));
}

/** A draft only, with its templates. */
export function discardTemplateSet(id) {
  return call(`${BASE}/template-sets/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** A new draft copied from a published set; {name?}. Answers the new draft. */
export function newTemplateSetVersion(id, body = {}) {
  return call(`${BASE}/template-sets/${encodeURIComponent(id)}/versions`, json("POST", body));
}

export function publishTemplateSet(id) {
  return call(`${BASE}/template-sets/${encodeURIComponent(id)}/publish`, { method: "POST" });
}

export function retireTemplateSet(id) {
  return call(`${BASE}/template-sets/${encodeURIComponent(id)}/retire`, { method: "POST" });
}

/** {key, name, category, kind: footprint|zone, length_ft+width_ft | radius_ft, default_layer?, dims_source?} */
export function addTemplate(setId, body) {
  return call(`${BASE}/template-sets/${encodeURIComponent(setId)}/templates`, json("POST", body));
}

/** Any of the add fields except kind. */
export function updateTemplate(setId, templateId, body) {
  return call(`${BASE}/template-sets/${encodeURIComponent(setId)}/templates/${encodeURIComponent(templateId)}`, json("PATCH", body));
}

export function removeTemplate(setId, templateId) {
  return call(`${BASE}/template-sets/${encodeURIComponent(setId)}/templates/${encodeURIComponent(templateId)}`, { method: "DELETE" });
}

/** The org's default lineage for new plans, or null to clear it. */
export function setDefaultTemplateLineage(lineageId) {
  return call(`${BASE}/template-sets/default`, json("PUT", { lineage_id: lineageId }));
}

/* ── clearance checks (S5a; admin-api/routes/padplannerChecks.mjs) ───────── */

/**
 * {plan_id, revision, rule_set, run, stale, rules: 'published'|'none', status, results}.
 * status is all (default), fail or pass. A read never runs the engine.
 */
export function getChecks(id, status) {
  const path = `${BASE}/plans/${encodeURIComponent(id)}/revisions/current/checks`;
  return call(withQuery(path, { status }));
}

/**
 * Any revision's checks (S6a2), as getChecks answers them: a submitted
 * revision's pinned submit run, never stale; the current draft's latest.
 */
export function getRevisionChecks(id, revNo, status) {
  const path = `${BASE}/plans/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revNo)}/checks`;
  return call(withQuery(path, { status }));
}

/** Run the checks now (trigger 'manual'). Answers {checks: {run, results}}, every result. */
export function runChecks(id) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/current/checks`, { method: "POST" });
}

/* ── clearance rules (S5a; admin-api/routes/padplannerRules.mjs) ─────────── */

// One lineage per org: at most one draft and one published set. Drafting needs
// can_edit_site_plans; publish and retire need org_admin too. Each write
// answers {set, rules} as the set now is, except discard ({discarded, set_id}).

/** {sets, published_set_id, can_draft, can_publish}. Drafts are listed only to a caller who may draft. */
export function listRuleSets() {
  return call(`${BASE}/rule-sets`);
}

/** {set, rules, can_draft, can_publish}. */
export function getRuleSet(id) {
  return call(`${BASE}/rule-sets/${encodeURIComponent(id)}`);
}

/** {name?}: the org's next version, a draft, copied from the published set's rules. The first needs a name. */
export function createRuleSet(body = {}) {
  return call(`${BASE}/rule-sets`, json("POST", body));
}

/** {name}: a draft only. */
export function updateRuleSet(id, body) {
  return call(`${BASE}/rule-sets/${encodeURIComponent(id)}`, json("PATCH", body));
}

export function discardRuleSet(id) {
  return call(`${BASE}/rule-sets/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** {rule_key, subject_selector, object_selector?, relation, limit_ft?, severity, message, sort_order?} */
export function addRule(setId, body) {
  return call(`${BASE}/rule-sets/${encodeURIComponent(setId)}/rules`, json("POST", body));
}

export function updateRule(setId, ruleId, body) {
  return call(`${BASE}/rule-sets/${encodeURIComponent(setId)}/rules/${encodeURIComponent(ruleId)}`, json("PATCH", body));
}

export function removeRule(setId, ruleId) {
  return call(`${BASE}/rule-sets/${encodeURIComponent(setId)}/rules/${encodeURIComponent(ruleId)}`, { method: "DELETE" });
}

export function publishRuleSet(id) {
  return call(`${BASE}/rule-sets/${encodeURIComponent(id)}/publish`, { method: "POST" });
}

export function retireRuleSet(id) {
  return call(`${BASE}/rule-sets/${encodeURIComponent(id)}/retire`, { method: "POST" });
}

/* ── the approval lifecycle (S6a1; routes/padplannerLifecycle.mjs) ──────── */

/**
 * {plan_id, plan_number, status, current_revision, last_approved_rev_no,
 * route: {id, name, plan_type, chosen}|null, revisions: [{id, rev_no, frozen,
 * submitted_at, submitted_by, submit_check_run_id, decided_status, issued_at,
 * issued_by_display_name, route, steps}], can: {submit, decide, issue, start_revision}}.
 * `can` is a hint; every transition checks again.
 */
export function getApproval(id) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/approval`);
}

/** {token}: the revision token from the checks read (revision.token). A stale one is 409 revision_changed. */
export function submitPlan(id, token) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/submit`, json("POST", { token }));
}

/** decision: 'approved' | 'changes_requested'; a request for changes needs a note of 10+ characters. */
export function decideStep(id, ordinal, decision, note) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/current/steps/${encodeURIComponent(ordinal)}/decision`,
    json("POST", note === undefined || note === null ? { decision } : { decision, note }));
}

/** An approved plan, issued to the field. org_admin. */
export function issuePlan(id) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/issue`, json("POST", {}));
}

/**
 * Generate the plan report of issued revision revNo, which has none (S7d, an
 * org_admin's): made once from that revision as issued and stored. Answers
 * {plan, report_generated: {revision_id, rev_no, report}}.
 */
export function generatePlanReport(id, revNo) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revNo)}/report`, json("POST", {}));
}

/** Start the next revision from the current one (its features and wells carried). Answers 201 {revision_started}. */
export function startRevision(id) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions`, json("POST", {}));
}

/* ── approval routes (S6a1; routes/padplannerApprovalRoutes.mjs) ─────────── */

/** {routes, can_manage}. includeArchived lists the archived ones too. */
export function listRoutes({ includeArchived = false } = {}) {
  const path = `${BASE}/routes`;
  return call(withQuery(path, { archived: includeArchived ? "include" : undefined }));
}

/** Who a step may name: {approvers: [{user_id, display_name, email}]}. org_admin. */
export function listApprovers() {
  return call(`${BASE}/routes/approvers`);
}

/** {name, plan_type|null, is_default, steps: [{role_label, approver_user_id|null}]}. Answers {route}. */
export function createRoute(body) {
  return call(`${BASE}/routes`, json("POST", body));
}

/** Any of the create fields; steps are replaced whole. Answers {route}. */
export function updateRoute(id, body) {
  return call(`${BASE}/routes/${encodeURIComponent(id)}`, json("PATCH", body));
}

export function archiveRoute(id) {
  return call(`${BASE}/routes/${encodeURIComponent(id)}/archive`, json("POST", {}));
}

/* ── comments (S6a1; routes/padplannerComments.mjs) ──────────────────────── */

/** {comments, can_comment}: a revision's pins, by pin number. */
export function listComments(id, revNo) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revNo)}/comments`);
}

/** {body, geometry?, feature_id?}: a point, a feature of that revision, or both. Any revision, frozen ones too. */
export function addComment(id, revNo, body) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revNo)}/comments`, json("POST", body));
}

/** The author, while it is open. */
export function editComment(id, commentId, body) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`, json("PATCH", { body }));
}

/** The author or an org_admin, once: a resolved comment never changes again. */
export function resolveComment(id, commentId) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}/resolve`, json("POST", {}));
}

/* ── submit to landowner (S6b; routes/padplannerLandowner.mjs) ───────────── */

/** {cases}: each case the plan went to, its live status and its FROZEN findings only. */
export function getLandownerSubmissions(id) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/landowner-submissions`);
}

/** {state: live|revoked|none, relationships, can_submit}: this org's grants from landowners, and the caller's seat. */
export function getLandownerRelationships() {
  return call(`${BASE}/landowner-relationships`);
}

/** {submission}: revision revNo (approved or issued) to that landowner, as a Plan Reviews case revision. */
export function submitToLandowner(id, revNo, ownerOrgId) {
  return call(`${BASE}/plans/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revNo)}/landowner-submissions`,
    json("POST", { owner_org_id: ownerOrgId }));
}

/**
 * Parse a perimeter file on the server. Nothing is saved; the answer is a
 * geometry the page shows and then sends as {perimeter}.
 *
 * NO HEADERS: a multipart body is parseable only with the boundary the browser
 * appends when IT sets Content-Type (portalApi.js inspectUpload).
 */
export function parsePerimeterFile(file) {
  const form = new FormData();
  form.append("file", file);
  return call(`${BASE}/perimeter/parse`, { method: "POST", body: form });
}
